import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { UserAgentApplication } from 'msal';
import Pusher from 'pusher-js';
import { Divider } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { useWindowDimensions } from 'hooks/common';

import { ConfigurationSelector } from 'store/configuration';
import {
  ThunkExternalLogin,
  ThunkLogin,
  ThunkLoginPreCheck,
  ThunkMfaLogin,
} from 'store/authorization/thunks';
import { ThunkGetAppLogo } from 'store/common/thunks';
import { AppLogoSelector, ThemeModeSelector } from 'store/common';
import {
  getGoogleSettings,
  getMicrosoftSettings,
} from 'services/authorization/authorization.service';
import { getAuthAppPusher } from 'services/common/common.service';

import { CONNECTWISE_POD, HALO_POD } from 'router/routes';
import { Integrations } from 'constants/integrations';
import { appConfig } from 'constants/appConfig';
import {
  SESSION_STORAGE_PSA,
  SESSION_STORAGE_PSA_ID,
  SESSION_STORAGE_SCREEN,
  SESSION_STORAGE_TICKET_ID,
  SESSION_STORAGE_URL,
} from 'constants/sessionStorage';

import Input from 'components/Input';
import Button from 'components/Button';
import FieldWrapper from 'components/FieldWrapper';
import Loader from 'components/Loader';

import { ReactComponent as GoogleIcon } from 'assets/icons/google.svg';
import { ReactComponent as MicrosoftIcon } from 'assets/icons/microsoft.svg';
import MspLogo from 'assets/logo/mspLogo.png';
import MspLogoBlackAndWhite from 'assets/logo/mspLogoBlackAndWhite.png';
import { ILoginTypes, IPusherEvent } from 'pages/auth/Login/hooks';

import useStyles from './styles';

interface FormValues {
  username: string;
  password: string;
  mfaCode: string;
}

const initialValues: FormValues = {
  username: '',
  mfaCode: '',
  password: '',
};

interface IGoogleToken {
  id_token: string;
}

export interface IGoogleSignInResponse {
  getAuthResponse: () => IGoogleToken;
}

export interface IGoogleError {
  error: string;
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [processing, setProcessing] = useState<boolean>(false);
  const [showMfaCode, setShowMfaCode] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const config = useAppSelector(ConfigurationSelector);
  const logo = useAppSelector(AppLogoSelector);
  const themeMode = useAppSelector(ThemeModeSelector);

  const { width } = useWindowDimensions();

  const isSimpleMode = useMemo(() => width < 350, [width]);
  const classes = useStyles({
    simpleMode: isSimpleMode,
  });

  const currentLogoUrl = useMemo(() => {
    if (logo) {
      return logo;
    }
    return themeMode === 'dark' ? MspLogoBlackAndWhite : MspLogo;
  }, [logo]);

  const redirectToPod = (): void => {
    const psa = sessionStorage.getItem(SESSION_STORAGE_PSA);
    const psaId = sessionStorage.getItem(SESSION_STORAGE_PSA_ID);
    const ticketId = sessionStorage.getItem(SESSION_STORAGE_TICKET_ID);
    const url = sessionStorage.getItem(SESSION_STORAGE_URL);

    if (psa === Integrations.ConnectWise && psaId) {
      const screen = sessionStorage.getItem(SESSION_STORAGE_SCREEN);
      navigate(`${CONNECTWISE_POD.replace(':psaId', psaId)}?ticketId=${ticketId}&screen=${screen}`);
    }
    if (psa === Integrations.Halo && psaId) {
      navigate(`${HALO_POD.replace(':psaId', psaId)}?ticketId=${ticketId}`);
    }

    if (url) {
      navigate(url);
    }
  };

  const authAppLogin = (type: ILoginTypes): void => {
    getAuthAppPusher().then(res => {
      const guid = crypto.randomUUID();

      const pusher = new Pusher(res.data.key, {
        cluster: res.data.cluster,
        channelAuthorization: {
          transport: 'ajax',
          endpoint: `${appConfig.baseUrl}/common/authorize-auth-pusher`,
        },
      });

      const channel = pusher.subscribe(`private-${guid}`);

      channel.bind('client-message', (data: IPusherEvent) => {
        dispatch(ThunkExternalLogin({ token: data.token, tokenType: data.type }))
          .unwrap()
          .then(async logged => {
            if (logged?.userId) {
              redirectToPod();
            }
          });
      });

      window.open(`${appConfig.authUrl}?type=${type}&sessionId=${guid}`, '_blank');
    });
  };

  type GoogleOAuth2Window = Window & {
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token: string }) => void;
          }) => {
            requestAccessToken: () => void;
          };
        };
      };
    };
  };

  const win = window as unknown as GoogleOAuth2Window;

  const loadGoogleScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (win.google && win.google.accounts && win.google.accounts.oauth2) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        if (win.google && win.google.accounts && win.google.accounts.oauth2) {
          resolve();
        } else {
          reject(new Error('Google script loaded but OAuth2 not available.'));
        }
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Identity Services script.'));
      };
      document.head.appendChild(script);
    });
  };

  const googleLogin = async (): Promise<void> => {
    try {
      await loadGoogleScript();
      const { data } = await getGoogleSettings();

      const client = win.google.accounts.oauth2.initTokenClient({
        client_id: data.clientId,
        scope: 'email profile openid',
        callback: (response: { access_token: string }) => {
          if (response && response.access_token) {
            dispatch(
              ThunkExternalLogin({
                token: response.access_token,
                tokenType: 'Google',
              }),
            )
              .unwrap()
              .then(res => {
                if (res?.userId) {
                  redirectToPod();
                }
              })
              .catch(err => {
                console.error('Login failed:', err);
                setLoading(false);
              });
          } else {
            console.error('No access token received');
            setLoading(false);
          }
        },
      });

      client.requestAccessToken();
    } catch (err) {
      console.error('Google OAuth init failed:', err);
      setLoading(false);
    }
  };

  const microsoftLogin = async (): Promise<void> => {
    try {
      getMicrosoftSettings().then(async res => {
        const msalObject = new UserAgentApplication({
          auth: {
            clientId: res.data.auth.clientId,
            authority: res.data.auth.authority,
            redirectUri: window.location.origin?.endsWith('/')
              ? window.location.origin
              : window.location.origin + '/',
          },
        });

        // eslint-disable-next-line no-console
        console.log('window created');

        const token = await msalObject.loginPopup({
          scopes: ['User.Read'],
        });

        dispatch(ThunkExternalLogin({ token: token.idToken.rawIdToken, tokenType: 'Microsoft' }))
          .unwrap()
          .then(res => {
            if (res?.userId) {
              redirectToPod();
            }
          });
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info(e);
    }
  };

  const thirdPartyLogin = (type: ILoginTypes): Promise<void> => {
    switch (type) {
      case 'Microsoft':
        return microsoftLogin();
      case 'Google':
        return googleLogin();
      default:
        return new Promise(() => {});
    }
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        username: Yup.string().email().required(),
        password: Yup.string().required(),
        mfaCode: showMfaCode ? Yup.string().required('MFA Code is required') : Yup.string(),
      }),
    [showMfaCode],
  );

  const desktopMode = useMemo(
    () => navigator.userAgent.split(' ').some(i => i.includes('ConnectWiseEmbeddedBrowser')),
    [],
  );

  const handleThirdPartyLogin = useCallback(
    (type: 'Microsoft' | 'Google') => {
      // using auth app to login
      if (desktopMode) {
        authAppLogin(type);
      } else {
        // using sso providers
        thirdPartyLogin(type);
      }
    },
    [desktopMode],
  );

  const handleSubmit = useCallback(
    (values: FormValues): void => {
      setProcessing(true);

      if (showMfaCode && values.mfaCode) {
        dispatch(ThunkMfaLogin(values))
          .unwrap()
          .then(() => {
            redirectToPod();
          })
          .finally(() => {
            setProcessing(false);
          });
      } else {
        dispatch(ThunkLoginPreCheck({ username: values.username, password: values.password }))
          .unwrap()
          .then(res => {
            if (res?.mfaRequired) {
              setShowMfaCode(true);
            } else if (res?.url) {
              window.location.replace(res.url);
            } else {
              return dispatch(ThunkLogin(values))
                .unwrap()
                .then(() => {
                  redirectToPod();
                });
            }
          })
          .finally(() => {
            setProcessing(false);
          });
      }
    },
    [config, showMfaCode],
  );

  const onMount = useCallback(() => {
    setLoading(true);

    dispatch(ThunkGetAppLogo()).finally(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    onMount();
  }, [onMount]);

  if (loading) return <Loader />;
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {formik => (
        <form onSubmit={formik.handleSubmit} className={classes.form}>
          <div className={classes.container}>
            <div className={classes.logo}>
              <img src={currentLogoUrl} alt="logo" />
            </div>

            <FieldWrapper
              component={
                <Input placeholder="Email" name="username" formik={formik} disabled={processing} />
              }
              label="Email"
              labelSize={12}
              contentSize={12}
              hideLabel={isSimpleMode}
            />

            <FieldWrapper
              component={
                <Input
                  placeholder="Password"
                  name="password"
                  formik={formik}
                  type="password"
                  disabled={processing}
                />
              }
              label="Password"
              labelSize={12}
              contentSize={12}
              hideLabel={isSimpleMode}
            />

            {showMfaCode && (
              <FieldWrapper
                component={
                  <Input
                    placeholder="MFA Code"
                    name="mfaCode"
                    formik={formik}
                    disabled={processing}
                  />
                }
                label="MFA Code"
                labelSize={12}
                contentSize={12}
                hideLabel={isSimpleMode}
              />
            )}

            <Button label="Login" type="submit" disabled={processing} />

            <Divider classes={{ root: classes.divider }} />

            <div
              className={cx(classes.logo, classes.external, {
                [classes.simplified]: width < 400,
              })}
            >
              <GoogleIcon
                onClick={() => {
                  handleThirdPartyLogin('Google');
                }}
              />
              <MicrosoftIcon
                onClick={() => {
                  handleThirdPartyLogin('Microsoft');
                }}
              />
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default Login;
